import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  formatUserMessageText,
  getServiceOrderId,
  isOrderEditable,
  getOrderMutationBlockedMessage,
  OrderService,
  resolveOrderGraphqlUserMessage,
  shouldRefreshAfterOrderError
} from 'src/entities/order';
import {
  OrderReservationStatus
} from 'src/entities/order/model/order-status.model';

export interface ReservationManualDialogData {
  editcode?: boolean;
  orderId?: string | null;
  service?: {
    order?: {
      orderId?: string | null;
      orderStatus?: string | null;
      reservationNumber?: string | null;
    };
  };
}

export interface ReservationManualDialogResult {
  success: boolean;
  shouldRefresh?: boolean;
  order?: any;
  orderNumber?: string | number | null;
}

type ViewState = 'form' | 'error';

@Component({
  selector: 'app-dialog-reservas-manual',
  templateUrl: './dialog-reservas-manual.component.html',
  styleUrls: ['./dialog-reservas-manual.component.css']
})
export class DialogReservasManualComponent implements OnInit {
  state: ViewState = 'form';
  errorTitle: string | null = null;
  errorMessage: string | null = null;

  readonly OrderReservationStatus = OrderReservationStatus;
  readonly reservationOptions = [
    { value: OrderReservationStatus.CONFIRMADO, label: 'Confirmado' },
    { value: OrderReservationStatus.RECHAZADO, label: 'Rechazado' },
    { value: OrderReservationStatus.EN_ESPERA, label: 'En Espera' },
  ];

  selectCheckBox: OrderReservationStatus = OrderReservationStatus.CONFIRMADO;
  codigoControl = new FormControl('', [
    Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9°!"#$%&/()=¿?¡!/*\-+.]*$/)
  ]);
  message = 'Si no tiene código de reserva, dejar en blanco';
  isLoading = false;
  numeroReserva = false;

  private readonly orderId: string | null;

  constructor(
    public dialogRef: MatDialogRef<
      DialogReservasManualComponent,
      ReservationManualDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: ReservationManualDialogData,
    private readonly orderService: OrderService,
    private readonly toastr: ToastrService
  ) {
    this.orderId =
      data.orderId ?? getServiceOrderId(data.service ?? null) ?? null;
  }

  ngOnInit(): void {
    this.numeroReserva = Boolean(this.data.editcode);
    const currentNumber = this.data.service?.order?.reservationNumber;
    if (currentNumber) {
      this.codigoControl.setValue(currentNumber);
    }
  }

  changeCheckBox(status: OrderReservationStatus): void {
    this.selectCheckBox = status;
    if (status === OrderReservationStatus.CONFIRMADO) {
      this.message = 'Si no tiene código de reserva, dejar en blanco';
    } else {
      this.message = '';
      this.codigoControl.setValue('');
    }
  }

  saveReserva(): void {
    this.codigoControl.markAsTouched();
    if (this.codigoControl.invalid) return;
    if (this.codigoControl.value && this.codigoControl.hasError('pattern')) {
      return;
    }

    if (this.selectCheckBox === OrderReservationStatus.CONFIRMADO) {
      this.saveReservation();
      return;
    }

    if (!this.codigoControl.value?.trim()) {
      this.saveReservation();
    }
  }

  private saveReservation(): void {
    if (!isOrderEditable(this.data.service ?? {})) {
      this.toastr.warning(
        getOrderMutationBlockedMessage(
          'editar',
          this.data.service?.order?.orderStatus
        )
      );
      return;
    }

    if (!this.orderId) {
      this.toastr.error('El servicio no tiene una OS asociada.');
      return;
    }

    this.isLoading = true;

    const input: {
      orderId: string;
      reservationStatus: OrderReservationStatus;
      reservationNumber?: string | null;
    } = {
      orderId: this.orderId,
      reservationStatus: this.selectCheckBox,
    };

    if (this.selectCheckBox === OrderReservationStatus.CONFIRMADO) {
      input.reservationNumber = this.codigoControl.value?.trim() || null;
    }

    this.orderService.updateOrderReservationStatus(input).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.dialogRef.close({
          success: true,
          shouldRefresh: false,
          order: result,
          orderNumber: result.orderNumber,
        });
      },
      error: (error: unknown) => {
        this.isLoading = false;
        const msg = resolveOrderGraphqlUserMessage(
          error,
          'No se pudo actualizar el estado de reserva.'
        );

        if (shouldRefreshAfterOrderError(error)) {
          this.errorTitle = msg.title;
          this.errorMessage = formatUserMessageText(msg);
          this.state = 'error';
          this.dialogRef.disableClose = true;
          return;
        }

        this.toastr.error(msg.title, formatUserMessageText(msg));
      },
    });
  }

  cerrarYActualizar(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close({
      success: false,
      shouldRefresh: true
    });
  }
}
